import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CreationFormWrapper from "../../actprep-ui/components/CreationFormWrapper";
import { usePost, usePut } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";

export default function ToDoEventForm({
  // Props for autocomplete usage
  open,
  setOpen,
  initialValue,
  setValue,
  initialUUID,
  multiple,
  // Props for PaginatedTable usage
  row,
  onEdit,
  onCreate,
}) {
  // Determine if we're in PaginatedTable mode
  const isPaginatedTableMode = Boolean(onCreate || onEdit);
  const editing = Boolean(initialUUID || row?.uuid);
  const createToDoEventMutation = usePost(apiUrls.admin.toDoEvents());
  const editToDoEventMutation = usePut(apiUrls.admin.toDoEvents());
  const [formData, setFormData] = useState({
    title: "",
  });
  const handleFocus = () => setError(null);
  const [error, setError] = useState(null);
  const handleClose = () => {
    if (setOpen) {
      setOpen(false);
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    let response;
    if (editing) {
      response = await editToDoEventMutation.mutateAsync({
        ...formData,
        to_do_event_id: initialUUID || row?.uuid,
      });
    } else {
      response = await createToDoEventMutation.mutateAsync(formData);
    }
    const { data } = response;
    if (!data.success) {
      setError(data.reason);
      return;
    }

    // Handle PaginatedTable mode
    if (isPaginatedTableMode) {
      if (editing && onEdit) {
        onEdit(data.data);
      } else if (!editing && onCreate) {
        onCreate(data.data);
      }
      if (editing) {
        showSuccessSnackbar("Event Updated!");
      } else {
        showSuccessSnackbar("New Event Created!");
      }
      return;
    }

    // Handle autocomplete mode
    const newVal = {
      uuid: data.data.uuid,
      label: data.data.title,
    };
    if (multiple) {
      setValue((curr) => [...curr, newVal]);
    } else {
      setValue(newVal);
    }
    setOpen(false);
    if (editing) {
      showSuccessSnackbar("Event Updated!");
      return;
    }
    showSuccessSnackbar("New Event Created!");
  };

  useEffect(() => {
    if (isPaginatedTableMode) {
      // Initialize form data for PaginatedTable mode
      setFormData({
        title: row?.title || "",
      });
    } else if (open) {
      // Initialize form data for autocomplete mode
      setFormData((formData) => ({
        ...formData,
        title: initialValue || "",
      }));
    }
  }, [open, initialValue, isPaginatedTableMode, row]);

  // For PaginatedTable mode, render just the form fields
  if (isPaginatedTableMode) {
    return (
      <form onSubmit={onSubmit}>
        <Grid container gap={3} marginTop={1}>
          <Grid size={12}>
            <TextField
              fullWidth
              autoFocus
              value={formData.title}
              onChange={(event) =>
                setFormData({
                  ...formData,
                  title: event.target.value,
                })
              }
              label="Title"
              type="text"
              variant="standard"
              onFocus={handleFocus}
              error={!!error}
              helperText={error}
            />
          </Grid>
          <Grid size={12}>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              {editing ? "Save" : "Create"}
            </Button>
          </Grid>
        </Grid>
      </form>
    );
  }

  // For autocomplete mode, use CreationFormWrapper
  return (
    <CreationFormWrapper
      open={open}
      onClose={handleClose}
      onSubmit={onSubmit}
      error={error}
      title={editing ? `Editing '${initialValue}' Event` : "Add a new Event"}
      confirmText={editing ? "Save" : "Create"}
    >
      <Grid container gap={3} marginTop={1}>
        <Grid size={12}>
          <TextField
            fullWidth
            autoFocus
            value={formData.title}
            onChange={(event) =>
              setFormData({
                ...formData,
                title: event.target.value,
              })
            }
            label="Title"
            type="text"
            variant="standard"
            onFocus={handleFocus}
          />
        </Grid>
      </Grid>
    </CreationFormWrapper>
  );
}
